import type { Message, SharedContentCard } from '../../../../../../types/api'
import LinkifyText from '../../../../../../shared/LinkifyText'
import DecryptedMessage from './DecryptedMessage'

export interface SharedContentPreview {
  type: 'post' | 'property'
  card: SharedContentCard
}

const asCard = (value: unknown): SharedContentCard | null =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as SharedContentCard) : null

/**
 * Read the shared post/property preview off a message.
 *
 * The payload is the one the mobile app sends — `metadata.post` or
 * `metadata.property` — so a card shared from either platform renders on the
 * other. Metadata may arrive as an object, a JSON string, or (for multi-file
 * messages) an array, so normalize before reading.
 */
export const getSharedContentPreview = (message?: Message | null): SharedContentPreview | null => {
  let metadata: unknown = message?.metadata
  if (typeof metadata === 'string') {
    try {
      metadata = JSON.parse(metadata)
    } catch {
      return null
    }
  }
  if (Array.isArray(metadata)) metadata = metadata[0]
  if (!metadata || typeof metadata !== 'object') return null

  const source = metadata as { post?: unknown; property?: unknown }
  const post = asCard(source.post)
  if (post) return { type: 'post', card: post }
  const property = asCard(source.property)
  if (property) return { type: 'property', card: property }
  return null
}

// Bunny Stream serves a video's poster as `<video>/thumbnail.jpg`. The payload
// carries no media-type flag, so this is how a video cover is recognized (on
// cards from either platform) to show the play badge.
// Post cards shared before realstateapi derived video posters stored the stream
// itself (`…/playlist.m3u8`) as the image, which can't render as a picture.
// Show the thumbnail Bunny serves beside it instead.
const toCardImage = (image?: string | null): string | null => {
  const clean = image?.split(/[?#]/)[0]
  if (clean && clean.toLowerCase().endsWith('.m3u8')) {
    return clean.replace(/\/playlist\.m3u8$/i, '/thumbnail.jpg').replace(/\.m3u8$/i, '/thumbnail.jpg')
  }
  return image ?? null
}

const isVideoPoster = (image?: string | null): boolean =>
  typeof image === 'string' && /\/thumbnail\.jpg(\?|$)/i.test(image)

const openSharedLink = (link?: string | null) => {
  if (!link) return
  try {
    const parsed = new URL(link)
    // Only ever follow http(s), never javascript:/data: from stored metadata.
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return
  } catch {
    return
  }
  window.open(link, '_blank', 'noopener,noreferrer')
}

/** Small type marker shown over the cover image, mirroring the mobile card. */
const TypeBadge = ({ type }: { type: 'post' | 'property' }) => (
  <span className="shared-content-card__badge">
    {type === 'post' ? (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="4" width="18" height="16" rx="2" />
        <path d="M3 9h18M8 14h8" />
      </svg>
    ) : (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M3 10.5 12 4l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z" />
        <path d="M9 21v-6h6v6" />
      </svg>
    )}
    {type === 'post' ? 'Post' : 'Property'}
  </span>
)

const SharedContentMessage = ({ message }: { message: Message }) => {
  const preview = getSharedContentPreview(message)
  if (!preview) return null

  const { type, card } = preview
  const image = toCardImage(card.image)
  const poster = card.poster
  const posterName = poster?.name
  const isClickable = Boolean(card.link)

  return (
    <div className="shared-content-message">
      {message.content && (
        <div className="shared-content-message__caption">
          <DecryptedMessage message={message}>
            {(decryptedContent) => <LinkifyText text={decryptedContent} />}
          </DecryptedMessage>
        </div>
      )}

      <div className="shared-content-message__card-wrap">
        <div
          className="shared-content-card"
          onClick={() => openSharedLink(card.link)}
          role={isClickable ? 'button' : undefined}
          tabIndex={isClickable ? 0 : undefined}
        >
          {image ? (
            <div className="shared-content-card__images">
              <div className="shared-content-card__cover">
                <img src={image} alt="" className="shared-content-card__image-main" />
                <TypeBadge type={type} />
                {isVideoPoster(image) && (
                  <span className="shared-content-card__play" aria-hidden="true">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </span>
                )}
              </div>
            </div>
          ) : null}

          <div className="shared-content-card__body">
            {/* Without a cover image the badge has nothing to sit on, so it
                leads the body instead. */}
            {!image && <div className="shared-content-card__badge-inline">{<TypeBadge type={type} />}</div>}
            {posterName && (
              <div className="shared-content-card__user">
                {poster?.avatar && (
                  <img src={poster.avatar} alt={posterName} className="shared-content-card__avatar" />
                )}
                <span className="shared-content-card__user-name">{posterName}</span>
              </div>
            )}
            {card.title && <div className="shared-content-card__title">{card.title}</div>}
            {card.subtitle && <div className="shared-content-card__text">{card.subtitle}</div>}
            {card.caption && <div className="shared-content-card__caption">{card.caption}</div>}
          </div>
        </div>
      </div>
    </div>
  )
}

export default SharedContentMessage
